import React, { useState, useEffect } from "react";
import { FaEdit, FaTrash, FaSearch } from "react-icons/fa";
import api from "../../api"; // axios instance with token + school_id interceptor

const ExpenseCategoryPage = () => {
  const [form, setForm] = useState({ name: "", description: "" });
  const [items, setItems] = useState([]);
  const [editId, setEditId] = useState(null);
  const [msg, setMsg] = useState(null);
  const [search, setSearch] = useState("");

  const baseURL = "/expense/category";

  // Fetch all categories
  const fetchCategories = async () => {
    try {
      const res = await api.get(`${baseURL}/list`);
      const data = res.data;
      setItems(Array.isArray(data.data) ? data.data : []);
    } catch (err) {
      console.error("Fetch error:", err);
      setItems([]);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMsg(null);

    if (!form.name.trim()) {
      setMsg({ type: "error", text: "Category name required" });
      return;
    }

    try {
      let res;
      if (editId) {
        res = await api.post(`${baseURL}/update`, { ...form, id: editId });
      } else {
        res = await api.post(`${baseURL}/add`, form);
      }

      const json = res.data;
      if (json?.status) {
        setMsg({
          type: "success",
          text: json.message || (editId ? "Updated successfully" : "Added successfully"),
        });
        fetchCategories();
        setForm({ name: "", description: "" });
        setEditId(null);
      } else {
        setMsg({ type: "error", text: json?.message || "Operation failed" });
      }
    } catch (err) {
      console.error(err);
      setMsg({ type: "error", text: "Network or server error" });
    }
  };

  const handleEdit = (item) => {
    setForm({ name: item.name || "", description: item.description || "" });
    setEditId(item.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this category?")) return;
    try {
      const res = await api.post(`${baseURL}/delete`, { id });
      const json = res.data;
      setMsg({
        type: json?.status ? "success" : "error",
        text: json?.message || (json?.status ? "Deleted" : "Failed to delete"),
      });
      fetchCategories();
    } catch (err) {
      setMsg({ type: "error", text: "Network error" });
    }
  };

  // Live search filter
  const filteredItems = items.filter((item) =>
    item.name?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div style={{ display: "flex", minHeight: "100vh" }}>
      <div style={{ flex: 1, backgroundColor: "#f4f6f8" }}>
        <main style={{ padding: "30px", maxWidth: "1000px", margin: "auto" }}>
          {/* Add/Edit Category Section */}
          <section style={cardStyle}>
            <h2 style={titleStyle}>{editId ? "✏️ Edit Category" : "➕ Add New Category"}</h2>
            {msg && (
              <div
                style={{
                  marginBottom: 12,
                  color: msg.type === "error" ? "#cc0000" : "#166534",
                }}
              >
                {msg.text}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div style={formGrid}>
                <FormGroup label="Category Name">
                  <input
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    style={fullWidthInput}
                    required
                  />
                </FormGroup>

                <FormGroup label="Description (optional)">
                  <input
                    type="text"
                    name="description"
                    value={form.description}
                    onChange={handleChange}
                    style={fullWidthInput}
                  />
                </FormGroup>
              </div>

              <div style={{ textAlign: "right", marginTop: "20px" }}>
                <button type="submit" style={buttonStyle}>
                  {editId ? "Update Category" : "Add Category"}
                </button>
              </div>
            </form>
          </section>

          {/* Search Bar */}
          <div style={{ ...cardStyle, marginBottom: "20px", display: "flex", alignItems: "center" }}>
            <FaSearch style={{ marginRight: 10, color: "#0077cc" }} />
            <input
              type="text"
              placeholder="Search category by name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ ...fullWidthInput, width: "100%" }}
            />
          </div>

          {/* List Categories */}
          <section style={cardStyle}>
            <h2 style={titleStyle}>📋 All Categories</h2>
            {filteredItems.length === 0 ? (
              <p>No categories found.</p>
            ) : (
              <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                {filteredItems.map((item, idx) => (
                  <li
                    key={item.id}
                    style={{
                      marginBottom: "15px",
                      padding: "15px",
                      borderRadius: "8px",
                      background: idx % 2 === 0 ? "#f9f9f9" : "#eef3f9",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div>
                      <strong style={{ fontSize: "16px", color: "#333" }}>{item.name}</strong>
                      <div style={{ color: "#777", fontSize: "13px" }}>
                        {item.description && <span>{item.description} | </span>}
                        Status: {item.status || "active"}
                      </div>
                    </div>
                    <div>
                      <FaEdit
                        onClick={() => handleEdit(item)}
                        style={{
                          color: "#0077cc",
                          cursor: "pointer",
                          marginRight: "15px",
                        }}
                      />
                      <FaTrash
                        onClick={() => handleDelete(item.id)}
                        style={{ color: "red", cursor: "pointer" }}
                      />
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </main>
      </div>
    </div>
  );
};

// Reusable Form Group
const FormGroup = ({ label, children, style }) => (
  <div style={{ ...style }}>
    <label
      style={{
        marginBottom: "6px",
        display: "block",
        fontWeight: 500,
        color: "#444",
      }}
    >
      {label}
    </label>
    {children}
  </div>
);

const formGrid = {
  display: "grid",
  gridTemplateColumns: "1fr",
  gap: "20px",
};

const fullWidthInput = {
  width: "100%",
  padding: "10px",
  borderRadius: "6px",
  border: "1px solid #ccc",
  fontSize: "15px",
  boxSizing: "border-box",
};

const buttonStyle = {
  padding: "10px 25px",
  backgroundColor: "#0077cc",
  color: "#fff",
  border: "none",
  borderRadius: "6px",
  fontSize: "15px",
  cursor: "pointer",
};

const cardStyle = {
  background: "#ffffff",
  padding: "25px",
  borderRadius: "12px",
  boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
  marginBottom: "30px",
};

const titleStyle = {
  color: "#0077cc",
  marginBottom: "25px",
};

export default ExpenseCategoryPage;
